import { useEffect } from 'react';

import IconButton from '../../../components/IconButton';
import Close from '../../../components/icons/Close';
import RawHtml from '../../../components/RawHtml';
import { useHeaderDispatch, useIsShowingSystemAlert } from '../state/hooks';

import style from './system-alert.module.css';

interface SystemAlertProps {
  alertId: string;
  end: null | string;
  message: string;
  onHideOrShow: () => void;
  start: null | string;
}

const localStorageKey = 'systemAlertsDismissed';

const getSystemAlertsDismissed = () => {
  const dismissedAlerts = localStorage.getItem(localStorageKey);

  return dismissedAlerts ? JSON.parse(dismissedAlerts) as string[] : [];
};

const SystemAlert = ({
  alertId,
  end,
  message,
  onHideOrShow,
  start,
}: SystemAlertProps) => {
  const shouldRender = useIsShowingSystemAlert();
  const headerDispatch = useHeaderDispatch();

  useEffect(() => {
    onHideOrShow();
  }, [onHideOrShow, shouldRender]);

  useEffect(() => {
    const now = new Date();

    const startDate = start ? new Date(start) : null;
    const endDate = end ? new Date(end) : null;

    const systemAlertsDismissed = getSystemAlertsDismissed();

    let opened = true;

    if (systemAlertsDismissed.includes(alertId)) {
      opened = false;
    }
    else if (endDate && now > endDate) {
      opened = false;
    }
    else if (startDate && now < startDate) {
      opened = false;
    }

    headerDispatch({
      payload: {
        isShowingSystemAlert: opened,
      },
      type: 'updateIsShowingSystemAlert',
    });
  }, [
    alertId,
    end,
    headerDispatch,
    start,
  ]);

  const handleClose = () => {
    const systemAlertsDismissed = getSystemAlertsDismissed();
    const updatedAlerts = [...systemAlertsDismissed, alertId];

    localStorage.setItem(localStorageKey, JSON.stringify(updatedAlerts));

    headerDispatch({
      payload: {
        isShowingSystemAlert: false,
      },
      type: 'updateIsShowingSystemAlert',
    });
  };

  if (!shouldRender) {
    return null;
  }

  return (
    <div className={style.systemAlert}>
      <RawHtml>
        {message}
      </RawHtml>

      <IconButton
        aria-label="Close system alert"
        className={style.systemAlertCloseButton}
        onClick={handleClose}
      >
        <Close iconColor="black" />
      </IconButton>
    </div>
  );
};

export default SystemAlert;
